import { useState } from "react";
import { testsApi } from "@/api/tests.api";
import { useToast } from "@/hooks/use-toast";
import { moderatorCopyFor } from "@/components/moderator/moderator-copy";
import { RejectTestDialog } from "@/components/moderator/RejectTestDialog";
import { formatTestScore, roundTestScore, testOutcome } from "@/components/moderator/test-grading";
import { ModeratorGetPaid, type ModeratorGetPaidMode } from "./screens/ModeratorGetPaid";
import type { useGetPaid } from "./useGetPaid";
import type { ModeratorTestItem } from "@/components/get-paid";
import { useTestSubmissionRecycleBin } from "./hooks/use-test-submission-recycle-bin";

interface ModeratorCorrectionViewProps {
  ctx: ReturnType<typeof useGetPaid>;
  /** "all" keeps grading and contract steps together (admin page). */
  mode?: ModeratorGetPaidMode;
}

export function ModeratorCorrectionView({ ctx, mode = "all" }: ModeratorCorrectionViewProps) {
  const copy = moderatorCopyFor(ctx.currentLang);
  const { toast } = useToast();
  const isSuperAdmin = ctx.user?.role === "super_admin";
  const recycleBin = useTestSubmissionRecycleBin(isSuperAdmin);
  const [rejecting, setRejecting] = useState(false);
  const [rejectBusy, setRejectBusy] = useState(false);
  const total = ctx.totalWeighted();

  // A failing test needs a reason: the server answers 400 to a rejection
  // without one, which the grading hook used to send. Passing tests keep
  // the hook's own flow.
  const finishCorrection = () => {
    if (ctx.canValidate() && testOutcome(total) === "reject") {
      setRejecting(true);
      return;
    }
    ctx.handleFinishCorrection();
  };

  const confirmReject = async (reason: string) => {
    const testId = ctx.details?.testId;
    if (!testId) return;
    setRejectBusy(true);
    try {
      await testsApi.moderateSubmission(testId, { status: "REJECTED", score: roundTestScore(total), reason });
      toast({ title: copy.rejectedToast, description: copy.rejectedToastDescription(formatTestScore(total, ctx.currentLang)) });
      setRejecting(false);
      ctx.handleBackFromDetails();
      ctx.loadPendingTests();
    } catch {
      toast({ title: ctx.t.error, description: copy.rejectFailed, variant: "destructive" });
    } finally {
      setRejectBusy(false);
    }
  };

  const moveTestToTrash = async (test: ModeratorTestItem, reason: string): Promise<boolean> => {
    try {
      await recycleBin.moveToTrash.mutateAsync({
        id: String(test.id),
        reason,
        expectedVersion: test.recordVersion ?? 0,
      });
      toast({ title: ctx.t.getPaid.testTrashSuccess });
      ctx.loadPendingTests();
      ctx.loadCorrectedTests();
      ctx.loadValidatedTests();
      return true;
    } catch {
      toast({ title: ctx.t.error, description: ctx.t.getPaid.testTrashError, variant: "destructive" });
      return false;
    }
  };

  const restoreTest = async (id: string) => {
    try {
      await recycleBin.restore.mutateAsync(id);
      toast({ title: ctx.t.getPaid.testRestoreSuccess });
      ctx.loadPendingTests();
      ctx.loadCorrectedTests();
      ctx.loadValidatedTests();
    } catch {
      toast({ title: ctx.t.error, description: ctx.t.getPaid.testRestoreError, variant: "destructive" });
    }
  };

  return (
    <>
      <ModeratorGetPaid
        mode={mode}
        t={ctx.t}
        details={ctx.details}
        onBack={ctx.handleBackFromDetails}
        itemScores={ctx.itemScores}
        setScore={ctx.setScore}
        savingScores={ctx.savingScores || rejectBusy}
        averages={ctx.averages()}
        totalWeighted={total}
        canValidate={ctx.canValidate()}
        handleSaveScores={ctx.handleSaveScores}
        handleFinishCorrection={finishCorrection}
        moderationLang={ctx.moderationLang}
        setModerationLang={ctx.setModerationLang}
        moderatorNavTab={ctx.moderatorNavTab}
        visiblePendingTests={ctx.visiblePendingTests}
        visibleCorrectedTests={ctx.visibleCorrectedTests}
        loadingTests={ctx.loadingTests}
        loadPendingTests={ctx.loadPendingTests}
        loadCorrectedTests={ctx.loadCorrectedTests}
        loadValidatedTests={ctx.loadValidatedTests}
        loadModeratorContracts={ctx.loadModeratorContracts}
        loadSubmissionDetails={ctx.loadSubmissionDetails}
        handleSendContractForTest={ctx.handleSendContractForTest}
        handleConfirmFinalValidate={ctx.handleConfirmFinalValidate}
        finalValidationDialog={ctx.finalValidationDialog}
        setFinalValidationDialog={ctx.setFinalValidationDialog}
        finalValidatingTestId={ctx.finalValidatingTestId}
        sendingContractTestId={ctx.sendingContractTestId}
        contractProgressByTest={ctx.contractProgressByTest}
        isSuperAdmin={isSuperAdmin}
        trashItems={recycleBin.items}
        trashLoading={recycleBin.isLoading}
        trashError={recycleBin.hasError}
        trashHasMore={recycleBin.hasMore}
        trashLoadingMore={recycleBin.isLoadingMore}
        onLoadMoreTrash={() => { void recycleBin.loadMore(); }}
        onTrash={moveTestToTrash}
        onRestore={restoreTest}
      />
      <RejectTestDialog
        open={rejecting}
        score={formatTestScore(total, ctx.currentLang)}
        busy={rejectBusy}
        copy={copy}
        cancelLabel={ctx.t.getPaid.cancel}
        onCancel={() => setRejecting(false)}
        onConfirm={confirmReject}
      />
    </>
  );
}
